import {
	LoadingIndicator,
	useAnnouncementOnInterval,
	useAnnounceText,
} from '@monthio/design-system'
import { eventHubService } from '@monthio/react-components'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const AnimationOverlay = styled.div`
	box-sizing: border-box;
	position: fixed;
	display: grid;
	place-items: center;
	place-content: center;
	z-index: 10;
	background: #f8f8f8;
	height: 100svh;
	width: 100svw;
	padding: 20px;
	gap: 20px;
	> div {
		height: unset;
		margin-bottom: 0;
	}

	${(props) => props.theme.breakpoints.up('sm')} {
		gap: 50px;
	}
`

interface BuildingBlockAnimationProps {
	showTransactionFetchingProgress?: boolean
}

export const BuildingBlockAnimation = ({
	showTransactionFetchingProgress,
}: BuildingBlockAnimationProps) => {
	const { t } = useTranslation()
	const [fetchProgress, setFetchProgress] = useState<number>(0)
	const transactionFetchingShare = 88.5

	useEffect(() => {
		if (!showTransactionFetchingProgress) {
			return
		}
		const unsubscribe = eventHubService.subscribe(
			'CreateBankSessionProgressLevel',
			(payload: number) => {
				setFetchProgress(payload * transactionFetchingShare)
				if (payload === 1) {
					startBuildingProgress()
				}
			},
		)
		return () => {
			unsubscribe()
		}
	}, [showTransactionFetchingProgress])

	const announce = useAnnounceText({ assertiveness: 'polite' })
	useEffect(() => {
		announce(`${t('flowProgress-header')} ${t('flowProgress-text')}`)
	}, [announce, t])
	useAnnouncementOnInterval(
		t('loading-spinner-aria-valuetext', {
			progress: fetchProgress.toFixed(0),
		}),
		{
			assertiveness: 'polite',
			activelyAnnounce: true,
			intervalLength: 5000,
		},
	)

	const startBuildingProgress = (counter = 1) => {
		addBuildingStepIncrement(counter)
		counter++
		if (counter <= 10) {
			setTimeout(() => startBuildingProgress(counter), 1500)
		} else {
			setFetchProgress(100)
		}
	}

	const addBuildingStepIncrement = (stepCount: number) => {
		const progress = (1 - 0.75 ** stepCount) * (100 - transactionFetchingShare)
		setFetchProgress(transactionFetchingShare + progress)
	}

	return (
		<AnimationOverlay>
			<LoadingIndicator
				header={t('flowProgress-header')}
				text={t('flowProgress-text')}
				value={fetchProgress}
			/>
		</AnimationOverlay>
	)
}
